import Seo from "@/components/Seo";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/sections/FinalCTA";
import { Stagger, StaggerItem } from "@/components/motion";
import Icon from "@/components/Icon";

const SOLUTIONS = [
  {
    icon: "User",
    title: "For Solo Founders",
    outcome: "Stay on top of everything without drowning in tools.",
    desc: "Replace a stack of subscriptions with one calm workspace. Command Center tells you what needs your attention. The Board challenges your thinking. Business Assistant helps you delegate the busywork.",
    wins: ["End the tab-switching tax", "Get a virtual leadership team on day one", "Never lose a follow-up again"],
  },
  {
    icon: "Rocket",
    title: "For Startups",
    outcome: "Give your team a shared operating system from day one.",
    desc: "Context scales as fast as headcount. Everyone sees the same pipeline, priorities, goals and business memory — from founders to first hires.",
    wins: ["Shared context, not scattered docs", "Momentum instead of process sprawl", "Roles and permissions as you grow"],
  },
  {
    icon: "Briefcase",
    title: "For Agencies",
    outcome: "Move clients from pipeline → project → delivery without the chaos.",
    desc: "Sales/CRM feeds Projects. Projects roll up into Command Center. Meeting Assist keeps every client conversation connected. Business Data tracks the unique things your agency operates on.",
    wins: ["Clean pipeline-to-delivery handoff", "Multi-client clarity in one workspace", "Custom trackers for creatives, vendors and campaigns"],
  },
  {
    icon: "Lightbulb",
    title: "For Consultants & Freelancers",
    outcome: "Keep every engagement organised, without heavy tooling.",
    desc: "Track engagements as deals, deliver as projects, and let WorkSpace remember your business context so briefings and follow-ups feel personal.",
    wins: ["One place for engagements and follow-ups", "Draft plans for won engagements", "Knowledge that compounds across clients"],
  },
  {
    icon: "Building2",
    title: "For Small Businesses",
    outcome: "Run operations without stitching six tools together.",
    desc: "Customers, work, delivery, goals and business-specific trackers all live in one connected system — no more spreadsheet islands.",
    wins: ["One connected system, less admin", "Custom trackers for inventory, vendors, assets", "Board perspective when key decisions matter"],
  },
  {
    icon: "Users",
    title: "For Working Professionals",
    outcome: "Bring order to a portfolio of clients, side projects and goals.",
    desc: "Even if you're not running a company yet, WorkSpace helps you plan, execute and reflect — with an intelligent assistant at every step.",
    wins: ["Personal command centre", "Board perspective for career decisions", "Move from ideas to executed outcomes"],
  },
];

export default function Solutions() {
  return (
    <>
      <Seo
        title="Solutions — Who Stupeco WorkSpace is for"
        description="Stupeco WorkSpace adapts to how you operate — solo founders, startups, agencies, consultants, small businesses and working professionals."
        path="/solutions"
      />
      <PageHeader
        eyebrow="Solutions"
        title="Shaped around how you work."
        subtitle="Different founders, different journeys. Same connected workspace."
      />

      <section className="border-b border-border py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Stagger className="grid gap-5 lg:grid-cols-2">
            {SOLUTIONS.map((s) => (
              <StaggerItem key={s.title} className="h-full">
                <div className="card-hover flex h-full flex-col gap-5 rounded-3xl border border-border bg-card p-8 hover:border-foreground/20" data-testid={`solution-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-muted/60 text-brand-teal">
                      <Icon name={s.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-display text-xl font-semibold tracking-tight">{s.title}</h3>
                      <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-brand-teal">{s.outcome}</p>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
                  <ul className="mt-auto space-y-2 border-t border-border pt-5">
                    {s.wins.map((w) => (
                      <li key={w} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <span className="h-1.5 w-1.5 rounded-full bg-brand-teal" /> {w}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
